import { doc, getDoc, setDoc, onSnapshot, arrayUnion, writeBatch, serverTimestamp, updateDoc, DocumentReference } from 'firebase/firestore';
import { db } from './firebaseCore';
import { serverAxios, URLS } from '../config';

interface ReferralData {
    username: string;
    isValid: boolean;
}

export const addUserToFirestore = async (
    userId: string,
    email: string,
    name: string,
    walletAddress: string
) => {
    if (!userId || !email) {
        console.error("Invalid userId or email");
        throw new Error("Invalid userId or email");
    }

    try {
        const userRef = doc(db, 'users', userId);
        const docSnap = await getDoc(userRef);

        // Convert wallet address to lowercase
        const normalizedWalletAddress = walletAddress.toLowerCase();

        if (docSnap.exists()) {
            // If the document exists, only update lastLoginAt
            await setDoc(userRef, {
                lastLoginAt: serverTimestamp()
            }, { merge: true });
            return true; // Signal successful completion
        } else {
            // If the document doesn't exist, this is a first-time signup
            try {
                await setDoc(userRef, {
                    created_time: serverTimestamp(),
                    display_name: name,
                    email: email,
                    pendingWithdraw: false,
                    uid: userId,
                    validReferralCount: 0,
                    walletAddress: normalizedWalletAddress,
                    userReferrals: [],
                    userReferralsStatus: [],
                });

                // Check for stored referral code
                const storedReferral = localStorage.getItem('pendingReferral');
                if (storedReferral) {
                    try {
                        await processReferralAfterSignup(userId, storedReferral);
                        localStorage.removeItem('pendingReferral');
                    } catch (referralError) {
                        console.error("Error processing referral, continuing:", referralError);
                        // Continue even if referral processing fails
                    }
                }

                // After DB operations are complete, call the server API to add the wallet address
                try {
                    await serverAxios.post(URLS.ADD_ADDRESS, {
                        address: normalizedWalletAddress
                    });
                } catch (apiError) {
                    console.error('Error calling add_address API:', apiError);
                    // Continue even if API call fails - user is already created in DB
                }

                return true; // Signal successful completion
            } catch (dbError) {
                console.error("Error creating user document:", dbError);
                throw dbError; // Rethrow DB errors as they are critical
            }
        }
    } catch (error) {
        console.error("Critical error in addUserToFirestore:", error);
        throw error; // Rethrow to be handled by the calling function
    }
};


export const processReferralAfterSignup = async (newUserId: string, referrerEmail: string) => {
    if (!newUserId || !referrerEmail) {
        console.error("Invalid user ID or referrer email");
        return;
    }

    try {
        const referrerDoc = doc(db, 'users', referrerEmail);
        const newUserDoc = doc(db, 'users', newUserId);

        // Get referrer's document
        const referrerSnap = await getDoc(referrerDoc);
        if (!referrerSnap.exists()) {
            console.error("Referrer document not found");
            return;
        }

        // Get current arrays
        const referrerData = referrerSnap.data();
        const currentStatus = referrerData.userReferralsStatus || [];

        // Count number of active referrals (number of 1's in status array)
        const activeReferrals = currentStatus.filter((status: number) => status === 1).length;

        // Check if referrer has reached the limit
        if (activeReferrals >= 5) {
            return;
        }

        // Batch write to update both documents atomically
        const batch = writeBatch(db);

        // Update referrer's document
        batch.update(referrerDoc, {
            userReferrals: arrayUnion(newUserDoc),
            userReferralsStatus: [...currentStatus, 0],
            lastUpdated: serverTimestamp()
        });

        // Update new user's document with referee reference
        batch.update(newUserDoc, {
            refereeId: referrerDoc,  // This creates a reference like /users/email@example.com
            lastUpdated: serverTimestamp()
        });

        // Commit both updates atomically
        await batch.commit();

    } catch (error) {
        console.error("Error processing referral:", error);
        throw error;
    }
};


export const subscribeToUserReferralCount = (
    userEmail: string,
    onUpdate: (count: number) => void,
    onError?: (error: Error) => void
): (() => void) => {
    if (!userEmail) {
        onUpdate(0);
        return () => { };
    }

    const userDocRef = doc(db, 'users', userEmail);

    const unsubscribe = onSnapshot(
        userDocRef,
        (docSnapshot) => {
            if (docSnapshot.exists()) {
                const userData = docSnapshot.data();
                onUpdate(userData.validReferralCount || 0);
            } else {
                onUpdate(0);
            }
        },
        (error) => {
            console.error("Error in referral count subscription:", error);
            if (onError) onError(error);
            onUpdate(0);
        }
    );

    return unsubscribe;
};


export const subscribeToUserDisplayName = (
    userEmail: string,
    onUpdate: (displayName: string) => void,
    onError?: (error: Error) => void
): (() => void) => {
    if (!userEmail) {
        onUpdate('User');
        return () => { };
    }

    const userDocRef = doc(db, 'users', userEmail);

    const unsubscribe = onSnapshot(
        userDocRef,
        (docSnapshot) => {
            if (docSnapshot.exists()) {
                const userData = docSnapshot.data();
                onUpdate(userData.display_name || 'User');
            } else {
                onUpdate('User');
            }
        },
        (error) => {
            console.error("Error in display name subscription:", error);
            if (onError) onError(error);
            onUpdate('User');
        }
    );

    return unsubscribe;
};


export const fetchUserDisplayName = async (userEmail: string): Promise<string> => {
    if (!userEmail) {
        return 'User';
    }

    try {
        const userDocRef = doc(db, 'users', userEmail);
        const docSnapshot = await getDoc(userDocRef);

        if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            return userData.display_name || 'User';
        }
        return 'User';
    } catch (error) {
        console.error("Error fetching display name:", error);
        return 'User';
    }
};


export const updateUserDisplayName = async (
    userEmail: string,
    newDisplayName: string
): Promise<void> => {
    if (!userEmail || !newDisplayName) {
        console.error("Invalid userEmail or displayName");
        return;
    }

    try {
        const userRef = doc(db, 'users', userEmail);
        await updateDoc(userRef, {
            display_name: newDisplayName,
            lastUpdated: serverTimestamp()
        });
    } catch (error) {
        console.error("Error updating display name:", error);
        throw error;
    }
};


export const getUserLastLogin = async (userEmail: string): Promise<Date | null> => {
    if (!userEmail) {
        return null;
    }

    try {
        const userDocRef = doc(db, 'users', userEmail);
        const docSnapshot = await getDoc(userDocRef);

        if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            if (userData.lastLoginAt) {
                return userData.lastLoginAt.toDate();
            }
        }
        return null;
    } catch (error) {
        console.error("Error fetching user last login:", error);
        return null;
    }
};

export const getUserCreationTime = async (userEmail: string): Promise<Date | null> => {
    if (!userEmail) {
        return null;
    }

    try {
        const userDocRef = doc(db, 'users', userEmail);
        const docSnapshot = await getDoc(userDocRef);

        if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            if (userData.created_time) {
                return userData.created_time.toDate();
            }
        }
        return null;
    } catch (error) {
        console.error("Error fetching user creation time:", error);
        return null;
    }
};

export const fetchUserReferrals = async (userEmail: string): Promise<ReferralData[]> => {
    if (!userEmail) {
        console.error("No userEmail provided");
        return [];
    }

    try {
        // Get the user document
        const userDocRef = doc(db, 'users', userEmail);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            return [];
        }

        const userData = userDoc.data();
        const userReferrals = userData.userReferrals || [];
        const userReferralsStatus = userData.userReferralsStatus || {};

        // Create an array of promises to fetch each referred user's display name
        const referralPromises = userReferrals.map(async (referralRef: DocumentReference, index: number) => {
            try {
                // Fetch the referred user's document using the reference
                const referredUserDoc = await getDoc(referralRef);

                if (!referredUserDoc.exists()) {
                    return null;
                }

                const referredUserData = referredUserDoc.data();

                return {
                    username: referredUserData.display_name || 'Unknown User',
                    isValid: userReferralsStatus[index] === 1 // Convert 1/0 to boolean
                };
            } catch (error) {
                console.error(`Error fetching referred user data for reference ${referralRef.path}:`, error);
                return null;
            }
        });

        // Wait for all promises to resolve and filter out null values
        const resolvedReferrals = (await Promise.all(referralPromises)).filter((ref): ref is ReferralData => ref !== null);

        return resolvedReferrals;

    } catch (error) {
        console.error("Error fetching user referrals:", error);
        return [];
    }
};

export const checkEnterDrawStatus = async (userEmail: string): Promise<boolean> => {
    if (!userEmail) {
        return false;
    }

    try {
        const userRef = doc(db, 'users', userEmail);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            return false;
        }

        const userData = userDoc.data();
        return userData.enter_draw === true;
    } catch (error) {
        console.error('Error checking enter draw status:', error);
        return false;
    }
};